import { IConcept, IQuestionDefinition } from '../constants/queryCreation.interface';

/**
 * It finds root nodes in a question definition graph.
 * A root node is defined as a node that is not referenced by any other node in the graph.
 * It correctly handles subQueries and their concepts.
 * @param queryDefinition
 * @returns string[] - array of URIs of root nodes
 */
export function findRootNodes(queryDefinition: IQuestionDefinition) {
  const allNodes = new Set(Object.keys(queryDefinition));
  const referencedNodes = new Set();

  function collectReferencesFromNode(concept: IConcept) {
    if (!concept.relations) return;

    for (const rel of Object.values(concept.relations)) {
      for (const inst of rel.relationInstances || []) {
        // 1. References to normal nodes in main graph
        if (inst.relationTypeUri && allNodes.has(inst.relationTypeUri)) {
          referencedNodes.add(inst.relationTypeUri); // Mark as referenced
        }
        // 2. References into subQuery containers
        if (inst.subQueryUri && queryDefinition[inst.subQueryUri]) {
          referencedNodes.add(inst.subQueryUri); // Mark subQuery container as referenced!
          // Traverse its subQueryDefinition nodes
          const subQuery = queryDefinition[inst.subQueryUri].subQueryDefinition || {};
          for (const subQueryConcept of Object.values(subQuery)) {
            collectReferencesFromSubNode(subQueryConcept, queryDefinition);
          }
        }
      }
    }
  }

  // Traverses sub-nodes inside subQueryDefinition
  function collectReferencesFromSubNode(
    subQueryConcept: IConcept,
    queryDefinition: IQuestionDefinition
  ) {
    if (!subQueryConcept.relations) return;
    for (const rel of Object.values(subQueryConcept.relations)) {
      for (const inst of rel.relationInstances || []) {
        // Reference to a node in the main graph by relationTypeUri
        if (inst.relationTypeUri && allNodes.has(inst.relationTypeUri)) {
          referencedNodes.add(inst.relationTypeUri);
        }
        // Reference back to parent node in main graph by parentQueryId
        if (inst.parentQueryId !== null && inst.parentQueryId !== undefined) {
          for (const [mainUri, mainNode] of Object.entries(queryDefinition)) {
            if (mainNode.subQueryId === inst.parentQueryId) {
              referencedNodes.add(mainUri); // Mark as referenced
            }
          }
        }
        // Nested subqueries, recursively traverse deeper
        if (inst.subQueryUri && queryDefinition[inst.subQueryUri]) {
          referencedNodes.add(inst.subQueryUri);
          const nestedSubgraph = queryDefinition[inst.subQueryUri].subQueryDefinition || {};
          for (const nestedSubNode of Object.values(nestedSubgraph)) {
            collectReferencesFromSubNode(nestedSubNode, queryDefinition);
          }
        }
      }
    }
  }

  // Traverse all nodes in the main graph
  for (const concept of Object.values(queryDefinition)) {
    collectReferencesFromNode(concept);
    // For subgraph containers, also traverse their sub-nodes
    if (concept.subQueryDefinition) {
      for (const subNode of Object.values(concept.subQueryDefinition)) {
        collectReferencesFromSubNode(subNode, queryDefinition);
      }
    }
  }

  const rootNodes = [];
  for (const uri of Object.keys(queryDefinition)) {
    if (!referencedNodes.has(uri)) {
      rootNodes.push(uri);
    }
  }

  return rootNodes;
}
